/** @jsx isolateComponent */

import { css } from '@emotion/core';
import { useContext, useEffect, useState } from 'react';

import { ChangePlan } from '@zapier/checkout';
import { Colors, Modal } from '@zapier/design-system';
import { responsiveStyles } from '@zapier/responsive-utils';
import { isolateComponent } from '@zapier/style-encapsulation';

import { Error } from '@zapier/upsells/src/components/Error';
import { UpgradeModalContext } from '@zapier/upsells/src/contexts';
import { useSize } from '@zapier/upsells/src/hooks';
import { Size } from '@zapier/upsells/src/types';
import { canUpgradeForReason } from '@zapier/upsells/src/utils';

import { Celebrate, ExtendTrial, UpgradeNudge } from './';
import { UpgradeModalProps } from '../UpgradeModal';

const MODAL_STATE = {
  celebrateTrial: 'celebrateTrial',
  celebrateUpgrade: 'celebrateUpgrade',
  error: 'error',
  extendTrial: 'extendTrial',
  nudge: 'nudge',
  upgrade: 'upgrade',
};

// Can't use default ModalContent because it doesn't support changing background color or padding.
const modalContentStyles = (modalState: string, size: Size) =>
  responsiveStyles(size, {
    small: css`
      background-color: ${modalState === MODAL_STATE.upgrade
        ? Colors.white // Prevents the checkout flow background from not matching the modal background.
        : Colors.grey000};
      border-radius: 0;
      overflow: ${modalState === MODAL_STATE.nudge
        ? 'hidden' // Scroll bar messes up some of the nudge images.
        : 'auto'};
      padding: ${modalState === MODAL_STATE.upgrade
        ? 0 // Checkout page has its own padding.
        : '30px'};
      width: 100%;
    `,
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    medium: css`
      border-radius: 30px;
      box-shadow: 0px 20px 30px 0px rgba(0, 0, 0, 0.5);
      margin: 30px;
      max-height: min(calc(100vh - 80px - 60px), 600px);
      max-width: 750px;
      padding: ${modalState === MODAL_STATE.nudge
        ? 0 // Nudge state has an image that goes all the way to the modal edge
        : 30};
      width: auto;
    `,
  });

export const UpgradeModalLayout = ({
  appName,
  onClose,
  onUpgradeFinish = () => {},
  shouldAutoUpgrade = true,
  upgradeReason,
  wasTriggeredByZapToggle = false,
}: UpgradeModalProps) => {
  const size = useSize();
  const [modalState, setModalState] = useState(MODAL_STATE.nudge);

  const {
    account,
    accountCurrency,
    canExtendTrial,
    destinationPlan,
    error,
    extendTrial,
    isLoading,
  } = useContext(UpgradeModalContext);

  useEffect(() => {
    if (error) {
      setModalState(MODAL_STATE.error);
    }
  }, [error]);

  const isCelebrationState =
    modalState === MODAL_STATE.celebrateTrial ||
    modalState === MODAL_STATE.celebrateUpgrade;

  if (
    isLoading ||
    !destinationPlan ||
    (!isCelebrationState && // Celebration state happens after upgrade, so canUpgradeForReason will return false and prevent the modal from rendering. We don't want that.
      !canUpgradeForReason({
        plan: account?.plan,
        upgradeReason,
      }))
  ) {
    return null;
  }

  const onCloseWithReload = () => {
    onClose();

    // Refresh page to load new plan information. Remove querystring params.
    window.location.href = window.location.origin + window.location.pathname;
  };

  const onExtendTrialClick = async () => {
    setModalState(MODAL_STATE.extendTrial);
  };

  const onExtendTrialComplete = async () => {
    try {
      await extendTrial();
      setModalState(MODAL_STATE.celebrateTrial);
    } catch {
      setModalState(MODAL_STATE.error);
    }
  };

  const onUpgradeClick = () => {
    setModalState(MODAL_STATE.upgrade);
  };

  const onUpgradeComplete = () => {
    onUpgradeFinish();
    setModalState(MODAL_STATE.celebrateUpgrade);
  };

  const celebrateButtonText = wasTriggeredByZapToggle
    ? 'Back to Zap'
    : 'Continue';

  const contentMap = {
    [MODAL_STATE.nudge]: (
      <UpgradeNudge
        appName={appName}
        canExtendTrial={canExtendTrial}
        destinationPlan={destinationPlan}
        onClose={onClose}
        onExtendTrialClick={onExtendTrialClick}
        onUpgradeClick={onUpgradeClick}
        shouldAutoUpgrade={shouldAutoUpgrade}
        upgradeReason={upgradeReason}
        wasTriggeredByZapToggle={wasTriggeredByZapToggle}
      />
    ),
    [MODAL_STATE.upgrade]: (
      <ChangePlan
        changeType="upgrade"
        currency={account?.currency ?? undefined}
        isCompact={true}
        onCancel={onClose}
        onFinishChangePlan={onUpgradeComplete}
        showSuccessModal={false}
        toPlanName={destinationPlan.name.toLowerCase()}
      />
    ),
    [MODAL_STATE.celebrateTrial]: (
      <Celebrate
        buttonText={celebrateButtonText}
        celebrationType="trial"
        onClose={onCloseWithReload}
      />
    ),
    [MODAL_STATE.celebrateUpgrade]: (
      <Celebrate
        buttonText={celebrateButtonText}
        celebrationType="upgrade"
        newPlan={account?.plan} // Current plan has been upgraded at this point, so don't use destinationPlan here.
        onClose={onCloseWithReload}
      />
    ),
    [MODAL_STATE.error]: (
      <Error
        message={
          error ??
          "Please try again. If the problem persists, click the button below and we'll help you through it."
        }
      />
    ),
    [MODAL_STATE.extendTrial]: (
      <ExtendTrial
        accountCurrency={accountCurrency}
        onClose={onCloseWithReload}
        onExtendTrial={onExtendTrialComplete}
        shouldAutoUpgrade={shouldAutoUpgrade}
        trialPlan={destinationPlan}
        upgradeReason={upgradeReason}
      />
    ),
  };

  const closeModal =
    modalState === MODAL_STATE.celebrateTrial ||
    modalState === MODAL_STATE.celebrateUpgrade
      ? onCloseWithReload
      : onClose;

  return (
    <Modal onClosed={closeModal}>
      <div css={modalContentStyles(modalState, size)}>
        {contentMap[modalState]}
      </div>
    </Modal>
  );
};
